import styled, { css } from 'styled-components';

import { ButtonSize, ButtonVariant } from './index';

import { iconFill } from '../../styles/mixin';
import { Icon } from '../Icon';

interface IButton {
    variant?: ButtonVariant;
    size?: ButtonSize;
    active?: boolean;
}

interface IIcon {
    size: IButton['size'];
}

const mediumIconStyle = css<IIcon>`
    --button-icon-size: 0.625rem;
`;

const largeIconStyle = css<IIcon>`
    --button-icon-size: 1rem;
`;

export const StyledIcon = styled(Icon)<IIcon>`
    --button-icon-size: 0.625rem;

    width: var(--button-icon-size);
    height: var(--button-icon-size);
    margin: 0;
    ${({ size }) => {
        switch (size) {
            case ButtonSize.SMALL:
            case ButtonSize.MEDIUM:
                return mediumIconStyle;
            case ButtonSize.LARGE:
                return largeIconStyle;
            default:
                return null;
        }
    }}
`;

const primaryButtonStyle = css<IButton>`
    --button-background: ${({ theme }) => theme.colors.buttonPrimaryBg};
    --button-color: white;

    border-color: ${({ theme }) => theme.colors.buttonPrimaryBg};

    &:hover {
        --button-background: ${({ theme }) =>
            theme.colors.buttonPrimaryBgHover};

        border-color: ${({ theme }) => theme.colors.buttonPrimaryBgHover};
    }

    &:active ${({ active }) => active && ', &'} {
        --button-background: ${({ theme }) =>
            theme.colors.buttonPrimaryBgActive};

        border-color: ${({ theme }) => theme.colors.buttonPrimaryBgActive};
    }

    &:focus {
        --button-background: ${({ theme }) =>
            theme.colors.buttonPrimaryBgHover};

        border-color: ${({ theme }) => theme.colors.buttonPrimaryBgHover};
    }
`;

const secondaryButtonStyle = css<IButton>`
    --button-background: transparent;
    --button-color: ${({ theme }) => theme.colors.fontPrimary};

    border-color: ${({ theme }) => theme.colors.lightShadow};

    &:hover {
        --button-background: ${({ theme }) => theme.colors.lightShadow};

        border-color: ${({ theme }) => theme.colors.lightShadow};
    }

    &:active ${({ active }) => active && ', &'} {
        --button-background: ${({ theme }) => theme.colors.mediumShadow};

        border-color: ${({ theme }) => theme.colors.mediumShadow};
    }

    &:focus {
        --button-background: ${({ theme }) => theme.colors.lightShadow};

        border-color: ${({ theme }) => theme.colors.lightShadow};
    }
`;

const tertiaryButtonStyle = css<IButton>`
    --button-background: transparent;
    --button-color: ${({ theme }) => theme.colors.fontPrimary};

    border-color: transparent;

    &:hover {
        --button-background: ${({ theme }) => theme.colors.lightShadow};

        border-color: ${({ theme }) => theme.colors.lightShadow};
    }

    &:active ${({ active }) => active && ', &'} {
        --button-background: ${({ theme }) => theme.colors.mediumShadow};

        border-color: ${({ theme }) => theme.colors.mediumShadow};
    }

    &:focus {
        --button-background: ${({ theme }) => theme.colors.lightShadow};

        border-color: ${({ theme }) => theme.colors.lightShadow};
    }
`;

const smallButtonStyle = css<IButton>`
    padding: 0.5rem 1rem;
`;

const mediumButtonStyle = css<IButton>`
    padding: 0.75rem 1rem;
`;

const largeButtonStyle = css<IButton>`
    padding: 1rem 2.5rem;
`;

export const StyledButton = styled.button<IButton>`
    --button-color: white;
    --button-background: ${({ theme }) => theme.boxShadow.primary};

    display: flex;
    flex-direction: row;
    column-gap: 0.375rem;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 0.5rem;
    font-weight: 700;
    border-width: 1px;
    border-style: solid;
    color: var(--button-color);
    background: var(--button-background);
    ${({ variant, theme, size }) => css`
        transition: all ${theme.transitions.hover};
        transition-property: background, color, border-color, box-shadow;
        font-size: ${theme.fontSizes[5]};

        ${StyledIcon} {
            ${iconFill('var(--button-color)')}
        }

        &:focus {
            outline: 3px solid ${theme.colors.outline};
        }

        ${() => {
            switch (variant) {
                case ButtonVariant.PRIMARY:
                    return primaryButtonStyle;
                case ButtonVariant.SECONDARY:
                    return secondaryButtonStyle;
                case ButtonVariant.TERTIARY:
                    return tertiaryButtonStyle;
                default:
                    return primaryButtonStyle;
            }
        }}
        ${() => {
            switch (size) {
                case ButtonSize.SMALL:
                    return smallButtonStyle;
                case ButtonSize.MEDIUM:
                    return mediumButtonStyle;
                case ButtonSize.LARGE:
                    return largeButtonStyle;
                default:
                    return mediumButtonStyle;
            }
        }}
    `};

    &:disabled {
        cursor: not-allowed;
        background: #bfbfbf;
        border-color: #bfbfbf;

        &:hover {
            box-shadow: none;
        }
    }
`;
